/*
 * One reading of a failed /start request, so every step names the same
 * failure the same way. utils/request rejects with the body plus `statusCode`
 * for an HTTP error, `{ error: 'Request timed out…' }` for its own timeout,
 * and a raw TypeError when the network is gone.
 */
export const classify = (err) => {
  const offline = typeof navigator !== "undefined" && navigator.onLine === false;
  if (offline) return { kind: "offline", code: "offline" };
  const status = err && typeof err.statusCode === "number" ? err.statusCode : null;
  if (status === null) {
    const timedOut = err && typeof err.error === "string" && /timed out/i.test(err.error);
    return { kind: timedOut ? "unavailable" : "offline", code: timedOut ? "timeout" : "offline" };
  }
  const code = (err && typeof err.error === "string" && err.error) || String(status);
  const retryAfter = err && typeof err.retry_after === "number" ? err.retry_after : null;
  if (status === 404) return { kind: "off", code: "offer_off", status };
  if (status === 401) return { kind: "lapsed", code: "lapsed", status };
  if (status === 410 || code === "signup_expired") return { kind: "restart", code: "signup_expired", status };
  if (status === 429) {
    return code === "too_many_attempts"
      ? { kind: "attempts", code, status }
      : { kind: "rate", code: "rate_limited", status, retryAfter };
  }
  if (status >= 500) return { kind: "unavailable", code, status, retryAfter };
  return { kind: "rejected", code, status };
};

// Minutes for "Try again in {minutes} minutes", from the server's retry_after.
export const minutesFrom = (retryAfter) => Math.max(1, Math.ceil((retryAfter || 60) / 60));

// "Did you mean …?" for the commonest domain slips (Notion-style inline tip).
const TYPOS = {
  "gmial.com": "gmail.com",
  "gmal.com": "gmail.com",
  "gamil.com": "gmail.com",
  "gmail.co": "gmail.com",
  "gmail.con": "gmail.com",
  "gnail.com": "gmail.com",
  "hotmial.com": "hotmail.com",
  "hotmal.com": "hotmail.com",
  "yahooo.com": "yahoo.com",
  "yaho.com": "yahoo.com",
  "outlok.com": "outlook.com",
  "outloo.com": "outlook.com",
  "iclod.com": "icloud.com",
  "icloud.co": "icloud.com",
};

export const typoSuggestion = (email) => {
  const at = (email || "").lastIndexOf("@");
  if (at < 1) return null;
  const domain = email.slice(at + 1).toLowerCase();
  return TYPOS[domain] ? `${email.slice(0, at)}@${TYPOS[domain]}` : null;
};

export const isEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((email || "").trim());

export const domainOf = (email) => {
  const at = (email || "").lastIndexOf("@");
  return at < 0 ? "" : email.slice(at + 1);
};
